import { useMemo, useRef, useState } from "react";
import type { NodeRef, Provenance } from "../api/types";
import { forceLayout, degreeMap } from "./layout";
import { communityTones, PROVENANCE_COLOR, SEVERITY_COLOR } from "./palette";
import { Icon } from "../components/icons";
import { T } from "../i18n";

// Whole-graph constellation: a force-directed layout of every node, sized by how
// connected it is, colored by its community, with labels kept quiet (hubs, the
// hovered node, and the selection) so the structure reads instead of a wall of
// text. Selecting a node focuses its neighbourhood; the affected-set highlight
// (GY-040) overrides the fill. Direction is kept via arrowheads at the target.

// Colors come from the shared palette: muted community tones (only the largest
// communities get one), severity fills for nodes with a confirmed finding,
// neutral edges. Orange is the brand and never encodes data.
export { SEVERITY_COLOR };

const VB = { w: 1000, h: 640 };

export interface GraphCanvasProps {
  nodes: NodeRef[];
  edges: [string, string][];
  edgeProvenance?: (s: string, t: string) => Provenance | undefined;
  communityOf: (id: string) => number;
  labelOf: (id: string) => string;
  selected: string | null;
  onSelect: (id: string) => void;
  highlight?: Set<string>; // affected-set highlight (GY-040)
  ariaLabel: string;
}

export function GraphCanvas(props: GraphCanvasProps) {
  const { nodes, edges, communityOf, labelOf, selected, onSelect, highlight } = props;
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [hover, setHover] = useState<string | null>(null);
  const drag = useRef<{ x: number; y: number; moved: boolean } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const positions = useMemo(() => forceLayout(nodes, edges, communityOf), [nodes, edges, communityOf]);
  const colorFor = useMemo(() => communityTones(nodes.map((n) => communityOf(n.id))), [nodes, communityOf]);
  const lastDragMoved = useRef(false);
  const degree = useMemo(() => degreeMap(edges), [edges]);
  const visibleIds = useMemo(() => new Set(nodes.map((n) => n.id)), [nodes]);

  // Fit the raw layout into the viewBox: center of bounds -> center of canvas.
  const fit = useMemo(() => {
    const pts = Object.values(positions);
    if (pts.length === 0) return { s: 1, cx: 0, cy: 0 };
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const p of pts) { minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x); minY = Math.min(minY, p.y); maxY = Math.max(maxY, p.y); }
    const pad = 60;
    const spanX = Math.max(1, maxX - minX);
    const spanY = Math.max(1, maxY - minY);
    const s = Math.min((VB.w - pad * 2) / spanX, (VB.h - pad * 2) / spanY);
    return { s, cx: (minX + maxX) / 2, cy: (minY + maxY) / 2 };
  }, [positions]);

  const place = (id: string) => {
    const p = positions[id];
    if (!p) return null;
    return { x: VB.w / 2 + (p.x - fit.cx) * fit.s, y: VB.h / 2 + (p.y - fit.cy) * fit.s };
  };

  // Hubs that always carry a (quiet) label, so the map is legible before any click.
  const topHubs = useMemo(() => {
    return new Set([...degree.entries()].sort((a, b) => b[1] - a[1]).slice(0, 14).map(([id]) => id));
  }, [degree]);

  // Focus: when a node is selected (or hovered), its incident edges + neighbours
  // light up and everything else recedes.
  const focus = selected ?? hover;
  const neighbours = useMemo(() => {
    if (!focus) return null;
    const set = new Set<string>([focus]);
    for (const [s, t] of edges) {
      if (s === focus) set.add(t);
      else if (t === focus) set.add(s);
    }
    return set;
  }, [focus, edges]);

  const onPointerDown = (e: React.PointerEvent) => {
    lastDragMoved.current = false;
    drag.current = { x: e.clientX - pan.x, y: e.clientY - pan.y, moved: false };
    (e.target as Element).setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (drag.current) {
      const nx = e.clientX - drag.current.x;
      const ny = e.clientY - drag.current.y;
      if (Math.abs(nx - pan.x) + Math.abs(ny - pan.y) > 2) drag.current.moved = true;
      setPan({ x: nx, y: ny });
    }
  };
  // Remember whether the gesture moved until the click that follows it, so a
  // pan that starts on a node never selects it.
  const onPointerUp = () => { lastDragMoved.current = !!drag.current?.moved; drag.current = null; };
  const reset = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  const nodeR = (id: string, isSel: boolean) => {
    const d = degree.get(id) ?? 0;
    const r = 2.6 + Math.sqrt(d) * 1.3;
    return Math.min(isSel ? r + 1.5 : r, 12);
  };

  return (
    <div className="graph-canvas">
      <div className="graph-toolbar">
        <button aria-label={T.common.zoomIn} onClick={() => setZoom((z) => Math.min(3, z + 0.25))}>
          <Icon.plus size={16} />
        </button>
        <button aria-label={T.common.zoomOut} onClick={() => setZoom((z) => Math.max(0.4, z - 0.25))}>
          <Icon.minus size={16} />
        </button>
        <button className="btn-icon" aria-label={T.common.reset} onClick={reset}>
          <Icon.reset size={15} />
          {T.common.reset}
        </button>
      </div>
      <svg
        ref={svgRef}
        role="img"
        aria-label={props.ariaLabel}
        className="graph-svg"
        viewBox={`0 0 ${VB.w} ${VB.h}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
      >
        <defs>
          <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 z" fill="#5f5b58" />
          </marker>
          <marker id="arrow-hot" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 z" fill="#e8e2dc" />
          </marker>
        </defs>
        <g transform={`translate(${pan.x},${pan.y}) scale(${zoom})`}>
          {edges.map(([s, t], i) => {
            if (!visibleIds.has(s) || !visibleIds.has(t)) return null;
            const a = place(s);
            const b = place(t);
            if (!a || !b) return null;
            const incident = focus && (s === focus || t === focus);
            const faded = focus && !incident;
            const prov = props.edgeProvenance?.(s, t);
            const color = incident ? "#e8e2dc" : prov ? PROVENANCE_COLOR[prov] : "#8a847e";
            return (
              <line
                key={`${s}->${t}-${i}`}
                x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                stroke={color}
                strokeWidth={incident ? 1.3 : 0.8}
                markerEnd={incident ? "url(#arrow-hot)" : undefined}
                opacity={faded ? 0.05 : incident ? 0.85 : 0.35}
                vectorEffect="non-scaling-stroke"
              />
            );
          })}
          {nodes.map((n) => {
            const p = place(n.id);
            if (!p) return null;
            const isSel = n.id === selected;
            const isHot = highlight?.has(n.id);
            const isHover = n.id === hover;
            const fill = n.severity ? SEVERITY_COLOR[n.severity] : colorFor(communityOf(n.id));
            const inFocus = !focus || neighbours?.has(n.id);
            const r = nodeR(n.id, isSel);
            const sev = n.severity ? SEVERITY_COLOR[n.severity] : null;
            const showLabel = isSel || isHover || !!sev || (!focus && topHubs.has(n.id)) || (!!neighbours && neighbours.has(n.id) && focus === n.id);
            return (
              <g
                key={n.id}
                transform={`translate(${p.x},${p.y})`}
                className="graph-node"
                tabIndex={0}
                role="button"
                aria-label={labelOf(n.id)}
                aria-pressed={isSel}
                opacity={inFocus ? 1 : 0.22}
                onPointerEnter={() => setHover(n.id)}
                onPointerLeave={() => setHover((h) => (h === n.id ? null : h))}
                onClick={() => { if (!lastDragMoved.current) onSelect(n.id); }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(n.id); }
                }}
              >
                {n.severity && <circle r={r + 5} fill={fill} opacity={0.22} />}
                {(isSel || isHot) && (
                  <circle r={r + 3} fill="none" stroke="#fff" strokeWidth={isSel ? 1.6 : 1} strokeDasharray={isSel ? undefined : "2 2.5"} vectorEffect="non-scaling-stroke" />
                )}
                <circle r={r} fill={fill}>
                  {n.severity && <title>{`${labelOf(n.id)} — ${n.severity} finding${n.findingStatus ? ` (${n.findingStatus})` : ""}`}</title>}
                </circle>
                {showLabel && (
                  <text x={r + 4} y={3.5} className={`graph-node-label ${isSel || isHover ? "lead" : ""}`}>
                    {labelOf(n.id)}
                  </text>
                )}
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
}
